import React, { useEffect } from 'react';
import classNames from 'classnames';

import { ControlledToggle } from 'src/components/ui/Toggle/Toggle';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useTranslation } from 'react-i18next';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { ControlledSelect } from 'src/components/ui/Select/Select';
import { ControlledTextarea } from 'src/components/ui/Textarea/Textarea';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';
import { Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import styles from './QualityCheckTable.module.scss';
import { createEmptyCheck } from '../QualityHelpers';
import { ProductFormValues } from '../../ProductFormTypes';

function getOptionLabel(t: any, interval: string) {
  return interval === 'every 2 weeks' ? t('interval.biweekly') : t(`interval.${interval}`);
}

const QualityCheckEdit: React.FC<{
  editModeActive: boolean;

  idx: number;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setExpandedIndex?: (index: number | null) => void;
  setValue: UseFormSetValue<ProductFormValues>;
}> = ({ editModeActive, idx, setExpandedIndex, control, errors, setValue }) => {
  const qualityChecks = useWatch({ control, name: `qualityChecks` }) ?? [];
  const check = qualityChecks?.[idx];
  const { t } = useTranslation();

  if (!check) {
    throw new Error(`Quality check with index ${idx} not found`);
  }

  const intervalOptions = ['daily', 'weekly', 'every 2 weeks', 'monthly', 'new_batch'].map(interval => {
    return {
      label: getOptionLabel(t, interval),
      value: interval,
    };
  });

  // the delete button is disabled if the overlay is not in edit mode or if the only quality check
  // is the default empty one
  const deleteDisabled =
    !editModeActive ||
    (qualityChecks.length === 1 &&
      equal(check, createEmptyCheck({ productId: check.productId, checkId: () => check.id })));

  return (
    <>
      <div className="flex items-center gap-16 mb-16">
        <ControlledInput
          control={control}
          name={`qualityChecks.${idx}.qualityCheckName`}
          error={!!errors.qualityChecks?.[idx]?.qualityCheckName}
          errorMessage={errors.qualityChecks?.[idx]?.qualityCheckName?.message}
          isInfoDisplay
          disabled={!editModeActive}
          className="col-span-2"
          type="string"
          placeholder={t('quality.quality_check_name')}
          label={`${t('quality.quality_check_name')} *`}
          maxLength={60}
        />
        <ControlledSelect
          name={`qualityChecks.${idx}.qualityCheckInterval`}
          control={control}
          isInfoDisplay
          label={t('quality.quality_check_interval')}
          disabled={!editModeActive}
          className="col-span-2"
          options={intervalOptions}
        />
      </div>
      <ControlledTextarea
        control={control}
        name={`qualityChecks.${idx}.qualityCheckInstruction`}
        disabled={!editModeActive}
        className="col-span-2"
        placeholder={t('quality.quality_check_instruction')}
        label={t('quality.quality_check_instruction')}
        style={{ backgroundColor: 'transparent' }}
        maxLength={2000}
      />
      <div className="flex justify-end mt-16 gap-8">
        <Button
          onClick={() => {
            const checks = structuredClone(qualityChecks ?? []);
            checks.splice(idx, 1);
            setValue('qualityChecks', checks);
            if (setExpandedIndex) {
              setExpandedIndex(null);
            }
          }}
          tone={ButtonToneProps.ERROR}
          title={t('quality.delete')}
          disabled={deleteDisabled}
        />
        <Button
          onClick={() => {
            setExpandedIndex?.(null);
          }}
          tone={ButtonToneProps.SECONDARY}
          title={t('general.close')}
        />
      </div>
    </>
  );
};

const QualityCheckTable: React.FC<{
  editModeActive: boolean;
  expandedIndex: number | null;
  onExpandToggleClick: (idx: number) => void;
  setExpandedIndex?: (idx: number | null) => void;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setValue: UseFormSetValue<ProductFormValues>;
}> = ({ expandedIndex, onExpandToggleClick, editModeActive, setExpandedIndex, control, errors, setValue }) => {
  const { t } = useTranslation();
  const checks = useWatch({ control, name: 'qualityChecks' }) ?? [];

  useEffect(() => {
    if (errors.qualityChecks && Array.isArray(errors.qualityChecks)) {
      const errorIndex = errors.qualityChecks.findIndex(error => error?.qualityCheckName);
      if (errorIndex !== -1 && setExpandedIndex && expandedIndex !== errorIndex) {
        setExpandedIndex(errorIndex);
      }
    }
  }, [errors.qualityChecks, expandedIndex, setExpandedIndex]);

  return (
    <>
      {(!checks || checks.length === 0) && (
        <div className="text-center typo-text-14 text-faded my-8">{t('quality.no_quality_checks')}</div>
      )}
      {checks && checks.length > 0 && (
        <div className={classNames(styles.root, 'mb-16')}>
          <table className="table-auto w-full border-collapse border-spacing-0 surface-elevated">
            <thead>
              <tr>
                <th className="typo-text-12 text-faded text-left py-2 px-4">{t('quality.quality_check_name')}</th>
                <th className="typo-text-12 text-faded text-left py-2 px-4">{t('quality.quality_check_interval')}</th>
                <th className="typo-text-12 text-faded text-left py-2 px-4">{t('quality.last_quality_check_date')}</th>
                <th className="typo-text-12 text-faded text-left py-2 px-4">{t('quality.quality_check_enabled')}</th>
                <th className="typo-text-12 text-faded text-left py-2 px-4" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {checks.map((check, idx) => (
                <React.Fragment key={check.id}>
                  <tr
                    className={`hover:surface-elevated-more ${styles.row}`}
                    onClick={() => {
                      onExpandToggleClick(idx);
                    }}>
                    <td className="typo-text-14 text-full py-2 px-4">{check.qualityCheckName}</td>
                    <td className="typo-text-14 text-full py-2 px-4">
                      {getOptionLabel(t, check.qualityCheckInterval ?? '')}
                    </td>
                    <td className="typo-text-14 text-full py-2 px-4">
                      {check.lastQualityCheckDate ? dayjs(check.lastQualityCheckDate).format('DD.MM.YYYY') : ''}
                    </td>
                    <td className="py-2 px-4">
                      {' '}
                      <div className="flex gap-2">
                        <ControlledToggle
                          name={`qualityChecks.${idx}.qualityCheckEnabled`}
                          control={control}
                          disabled={!editModeActive}
                        />
                      </div>
                    </td>
                    <td>
                      {' '}
                      <Button
                        className="ml-auto"
                        tone={ButtonToneProps.SECONDARY}
                        icon={expandedIndex === idx ? 'accordion-arrow-up' : 'accordion-arrow-down'}
                        onClick={() => {
                          onExpandToggleClick(idx);
                        }}
                      />
                    </td>
                  </tr>
                  {expandedIndex === idx && (
                    <tr key={`${check.id}-expanded`} className="border-subtle-bottom">
                      <td colSpan={5} aria-label="Quality Check Edit">
                        <QualityCheckEdit
                          setValue={setValue}
                          control={control}
                          errors={errors}
                          editModeActive={editModeActive}
                          // updateProductData={updateProductData}
                          idx={idx}
                          // tmpProduct={tmpProduct}
                          setExpandedIndex={setExpandedIndex}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
export default QualityCheckTable;
